import React from 'react'
import './styles/notification-delivery-builder.scss'
import { Well } from '@pushly/aqe/lib/components'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Switch, Popover, Input, Select, Skeleton, Radio } from 'antd'
import { NotificationExperienceModel } from '../../../models/notification/notification-experience.model'
import { calculateValueFromRawTtlSeconds } from '../../data-form-elements'
import { calculateRawSecondsFromValue, calculateValueFromRawSeconds } from '../../simple-timeframe/simple-timeframe'
import { getClassNames } from '../../../_utils/classnames'
import { DomainDto } from '../../../dtos/domain'
import { getVariantTtlConfig } from '../helpers'
import { FEAT_NOTIF_REQUIRE_INTERACTION_TOGGLE, FEAT_SILENT_DELIVERY_NOTIF } from '../../../constants'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../../stores/app'
import {
    COLLAPSE_KEY_REPLACE_ALL,
    COLLAPSE_KEY_STACK,
    NotificationCollapseBehavior,
    resolveBehaviorFromCollapseKey,
} from '@pushly/aqe/lib/utils/notification-collapse-key'

interface INotificationDeliveryBuilder {
    className?: string
    domain: DomainDto
    disabled?: boolean
    value?: NotificationExperienceModel | any
    onChange?: (value: NotificationExperienceModel) => any
    loading?: boolean
    title?: string
    hideHeading?: boolean
    hideTtlSettings?: boolean
    hideCollapseKeySettings?: boolean
}

const collapseBehaviorPopoverContent = (
    <div className="collapse-key-behavior-popover-content">
        <p>
            <strong>Stack</strong>
            Shows each notification.
        </p>
        <p>
            <strong>Replace All</strong>
            Closes all currently shown notifications before displaying this one.
        </p>
        <p>
            <strong>Replacement Group</strong>
            Enter a group key to have only notifications with the same key replace each other. Use letters, numbers,
            dashes, and underscores only.
        </p>
    </div>
)

const NotificationExperienceOptions = (props: INotificationDeliveryBuilder) => {
    const appState = useService(AppState)
    const {
        className,
        domain,
        loading,
        disabled,
        value,
        onChange,
        title,
        hideHeading,
        hideTtlSettings,
        hideCollapseKeySettings,
    } = props
    const [tmpValue, setTmpValue] = React.useState<NotificationExperienceModel>(
        NotificationExperienceModel.build(value ?? {}),
    )
    const [replacementGroupDraft, setReplacementGroupDraft] = React.useState<string>('')

    const workingModel = (!!value ? value : tmpValue).clone()

    const domainHasRIFlag = domain.flags?.includes(FEAT_NOTIF_REQUIRE_INTERACTION_TOGGLE)
    const ttlConfig = getVariantTtlConfig(domain, workingModel)
    const calculatedTtl = calculateValueFromRawSeconds(ttlConfig.seconds, ttlConfig.metric)

    const isSilentFlag = appState.flags.findActive(FEAT_SILENT_DELIVERY_NOTIF)
    const hasIsSilentFlag = isSilentFlag?.getIsActive() && domain.flags?.includes(isSilentFlag.getKey())
    const collapseBehaviorData = resolveBehaviorFromCollapseKey(workingModel.getCollapseKey(), domain.collapseKeyType)
    const collapseBehavior = collapseBehaviorData.behavior

    React.useEffect(() => {
        if (collapseBehavior === NotificationCollapseBehavior.REPLACEMENT_GROUP) {
            setReplacementGroupDraft(collapseBehaviorData.replacementGroupKey ?? '')
        }
    }, [collapseBehavior, collapseBehaviorData.replacementGroupKey])

    return (
        <>
            <Well
                className={getClassNames('notification-experience-builder', className)}
                title={title ?? 'Delivery'}
                mode="ghost"
                hideHeader={hideHeading}
                hideFooter={true}
            >
                <Skeleton loading={loading} active={true} avatar={false} title={false}>
                    {hasIsSilentFlag && (
                        <div className="switch-row">
                            <div className="switch-left">
                                <Switch
                                    size="small"
                                    checked={workingModel.getIsSilent()}
                                    onChange={(isSilent) => {
                                        const update = workingModel.clone()
                                        update.setIsSilent(isSilent)

                                        setTmpValue(update)
                                        onChange?.(update)
                                    }}
                                />
                            </div>
                            <div className="switch-right">
                                <span>Silent Delivery</span>
                                <Popover
                                    overlayClassName={getClassNames(
                                        'notification-builder-popover',
                                        'is-silent-popover',
                                    )}
                                    content={
                                        <span>
                                            When enabled, this notification will be delivered with no alert sound or
                                            vibration on supported mobile devices.
                                        </span>
                                    }
                                >
                                    <InfoCircleOutlined className="info-icon" />
                                </Popover>
                            </div>
                        </div>
                    )}

                    {domainHasRIFlag && (
                        <div className="switch-row">
                            <div className="switch-left">
                                <Switch
                                    size="small"
                                    disabled={disabled}
                                    checked={workingModel.getRequireInteraction()}
                                    onChange={(requireInteraction) => {
                                        const update = workingModel.clone()
                                        update.setRequireInteraction(requireInteraction)

                                        setTmpValue(update)
                                        onChange?.(update)
                                    }}
                                />
                            </div>
                            <div className="switch-right">
                                <span>Require Interaction</span>
                                <Popover
                                    overlayClassName={getClassNames(
                                        'notification-builder-popover',
                                        'require-interaction-popover',
                                    )}
                                    content={
                                        <span>
                                            When disabled, this notification will minimize to the notification center
                                            after approximately 8 seconds. Chrome for Android is not affected as all
                                            notifications are minimized to the notification tray regardless of this
                                            setting.
                                        </span>
                                    }
                                >
                                    <InfoCircleOutlined className="info-icon" />
                                </Popover>
                            </div>
                        </div>
                    )}

                    {!hideCollapseKeySettings && (
                        <>
                            <Form.Item
                                className="display-replacement-behavior"
                                label={
                                    <span>
                                        Display / Replacement Behavior
                                        <Popover
                                            overlayClassName={getClassNames(
                                                'notification-builder-popover',
                                                'collapse-key-behavior-popover',
                                            )}
                                            content={collapseBehaviorPopoverContent}
                                        >
                                            <InfoCircleOutlined className="info-icon" />
                                        </Popover>
                                    </span>
                                }
                            >
                                <div className="collapse-key-controls">
                                    <Radio.Group
                                        size="small"
                                        buttonStyle="solid"
                                        disabled={disabled}
                                        value={collapseBehavior}
                                        onChange={(ev) => {
                                            const nextBehavior = ev.target.value as NotificationCollapseBehavior
                                            const update = workingModel.clone()

                                            if (nextBehavior === NotificationCollapseBehavior.STACK) {
                                                update.setCollapseKey(COLLAPSE_KEY_STACK)
                                            } else if (nextBehavior === NotificationCollapseBehavior.REPLACE_ALL) {
                                                update.setCollapseKey(COLLAPSE_KEY_REPLACE_ALL)
                                            } else {
                                                const nextKey = replacementGroupDraft
                                                update.setCollapseKey(nextKey)
                                            }

                                            setTmpValue(update)
                                            onChange?.(update)
                                        }}
                                    >
                                        <Radio.Button value={NotificationCollapseBehavior.STACK}>Stack</Radio.Button>
                                        <Radio.Button value={NotificationCollapseBehavior.REPLACE_ALL}>
                                            Replace All
                                        </Radio.Button>
                                        <Radio.Button value={NotificationCollapseBehavior.REPLACEMENT_GROUP}>
                                            Replacement Group
                                        </Radio.Button>
                                    </Radio.Group>

                                    {collapseBehavior === NotificationCollapseBehavior.REPLACEMENT_GROUP && (
                                        <div className="replace-key-inline">
                                            <span className="replace-key-inline-label">Group Key:</span>
                                            <Input
                                                aria-label="Group Key"
                                                autoFocus={true}
                                                size="small"
                                                placeholder="e.g., sale-banner, sports-score, todays-forecast"
                                                disabled={disabled}
                                                value={replacementGroupDraft}
                                                onChange={(ev) => {
                                                    const nextKey = ev.target.value
                                                    const update = workingModel.clone()
                                                    setReplacementGroupDraft(nextKey)
                                                    update.setCollapseKey(nextKey)

                                                    setTmpValue(update)
                                                    onChange?.(update)
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </Form.Item>
                        </>
                    )}

                    {!hideTtlSettings && (
                        <Form.Item
                            className="lifespan"
                            label={
                                <span className="lifespan-label">
                                    Lifespan
                                    <Popover
                                        overlayClassName={getClassNames('notification-builder-popover', 'ttl-popover')}
                                        content={
                                            <div>
                                                <p>
                                                    Notification lifespan determines how long the notification will
                                                    attempt to be delivered to the subscriber.
                                                </p>
                                                <p>
                                                    <b>Example:</b> A notification with a lifespan of 1 day will not be
                                                    delivered to a user who does not become eligible for delivery within
                                                    24 hours of the notification being sent.
                                                </p>
                                            </div>
                                        }
                                    >
                                        <InfoCircleOutlined className="info-icon" />
                                    </Popover>
                                </span>
                            }
                        >
                            <Input
                                placeholder="0"
                                disabled={disabled}
                                value={calculatedTtl}
                                onChange={(ev) => {
                                    let v: any = ev.target.value
                                    const update = workingModel.clone()

                                    try {
                                        v = calculateRawSecondsFromValue(v, workingModel.getTtlMetric())
                                    } catch (_) {}
                                    update.setTtlSeconds(v)

                                    setTmpValue(update)
                                    onChange?.(update)
                                }}
                                addonAfter={
                                    <Select
                                        dropdownClassName="lifespan-metric-overlay"
                                        disabled={disabled}
                                        value={workingModel.getTtlMetric() ?? 'days'}
                                        onChange={(metric) => {
                                            const update = workingModel.clone()
                                            const previousTtl = calculateValueFromRawTtlSeconds(
                                                workingModel.getTtlSeconds(),
                                                workingModel.getTtlMetric(),
                                            )

                                            update.setTtlMetric(metric)
                                            try {
                                                update.setTtlSeconds(calculateRawSecondsFromValue(previousTtl, metric))
                                            } catch (_) {}

                                            setTmpValue(update)
                                            onChange?.(update)
                                        }}
                                    >
                                        <Select.Option value="minutes">Minute(s)</Select.Option>
                                        <Select.Option value="hours">Hour(s)</Select.Option>
                                        <Select.Option value="days">Day(s)</Select.Option>
                                    </Select>
                                }
                            />
                        </Form.Item>
                    )}
                </Skeleton>
            </Well>
        </>
    )
}

export default NotificationExperienceOptions
